import { useContext, useEffect, useState, VFC } from "react";

// Libraries
import { DateTime } from "luxon";
import { useLazyQuery } from "@apollo/client";

// API
import {
  GET_BLOCKCHAIN_ANIMAL_DATA_BY_KILL_NUMBER,
  GET_BLOCKCHAIN_ANIMAL_DATA_BY_PASSPORT,
} from "api/Blockchain/queries";
import {
  GetBlockchainAnimalDataByKillNumber,
  GetBlockchainAnimalDataByKillNumberVariables,
  GetBlockchainAnimalDataByKillNumber_blockchainAnimalDataByKillNumber,
} from "api/Blockchain/types/GetBlockchainAnimalDataByKillNumber";
import {
  GetBlockchainAnimalDataByPassport,
  GetBlockchainAnimalDataByPassportVariables,
  GetBlockchainAnimalDataByPassport_blockchainAnimalDataByPassport,
} from "api/Blockchain/types/GetBlockchainAnimalDataByPassport";

// Utils
import { CommonContext } from "config/commonProvider";

// Componenets
import { Flex, Spacer } from "components";
import { BlockchainAnimalCarcassGradingTable } from "./BlockchainAnimalCarcassGradingTable";
import { BlockchainAnimalMedicationTable } from "./BlockchainAnimalMedicationTable";
import { BlockchainAnimalMovementsTable } from "./BlockchainAnimalMovementsTable";
import { BlockchainAnimalOverview } from "./BlockchainAnimalOverview";
import { BlockchainAnimalWeightTable } from "./BlockchainAnimalWeightTable";
import { BlockchainSearchForm } from "./BlockchainSearchForm";

export interface SearchByPassportNumber {
  passportNumber: string;
  killNumber: null;
  killDate: null;
}

export interface SearchByKillData {
  passportNumber: null;
  killNumber: string;
  killDate: Date;
}

export type AnimalDataTypes =
  | GetBlockchainAnimalDataByPassport_blockchainAnimalDataByPassport
  | GetBlockchainAnimalDataByKillNumber_blockchainAnimalDataByKillNumber;

export const BlockchainSearch: VFC = () => {
  const { showNotification } = useContext(CommonContext);

  const [data, setData] = useState<AnimalDataTypes | null>(null);

  const [getBlockchainAnimalDataByPassport, { loading: loadingPassport, data: dataPassport, error: errorPassport }] =
    useLazyQuery<GetBlockchainAnimalDataByPassport, GetBlockchainAnimalDataByPassportVariables>(
      GET_BLOCKCHAIN_ANIMAL_DATA_BY_PASSPORT,
      {
        fetchPolicy: "network-only",
      },
    );

  const [
    getBlockchainAnimalDataByKillNumber,
    { loading: loadingKillNumber, data: dataKillNumber, error: errorKillNumber },
  ] = useLazyQuery<GetBlockchainAnimalDataByKillNumber, GetBlockchainAnimalDataByKillNumberVariables>(
    GET_BLOCKCHAIN_ANIMAL_DATA_BY_KILL_NUMBER,
    {
      fetchPolicy: "network-only",
    },
  );

  const isLoading: boolean = loadingPassport || loadingKillNumber;

  const handleSearch = (params: SearchByPassportNumber | SearchByKillData) => {
    if (params.passportNumber) {
      getBlockchainAnimalDataByPassport({
        variables: { passportNumber: params.passportNumber },
      });
    } else if (params.killNumber && params.killDate) {
      getBlockchainAnimalDataByKillNumber({
        variables: { killNumber: params.killNumber, date: DateTime.fromJSDate(params.killDate).toISODate() },
      });
    }
  };

  useEffect(() => {
    setData(dataPassport?.blockchainAnimalDataByPassport || null);
  }, [dataPassport]);

  useEffect(() => {
    setData(dataKillNumber?.blockchainAnimalDataByKillNumber || null);
  }, [dataKillNumber]);

  useEffect(() => {
    if (data?.error) {
      showNotification({
        variant: "error",
        message: data?.error,
      });
    } else if (errorPassport || errorKillNumber) {
      showNotification({
        variant: "error",
        message: "Error returning the data",
      });
    }
  }, [data, errorPassport, errorKillNumber]);

  return (
    <Flex container>
      <Flex item itemGutter xs={6}>
        <BlockchainSearchForm onSearch={handleSearch} isLoading={isLoading} />
      </Flex>

      <Flex item itemGutter xs={6}>
        <BlockchainAnimalOverview animal={data} />
      </Flex>

      {data && !data.error && (
        <>
          <Flex item itemGutter xs="fill">
            <Spacer baselineHeight={4}></Spacer>

            <BlockchainAnimalMovementsTable movements={data.movements} />

            <Spacer baselineHeight={4}></Spacer>

            <BlockchainAnimalWeightTable weights={data.weights} />

            <Spacer baselineHeight={4}></Spacer>

            <BlockchainAnimalMedicationTable medication={data.medication} />

            <Spacer baselineHeight={4}></Spacer>

            <BlockchainAnimalCarcassGradingTable carcassGrading={data.carcassGrading} />
          </Flex>
        </>
      )}

      <Spacer baselineHeight={18}></Spacer>
    </Flex>
  );
};
